import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import {
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Stack,
  IconButton,
  InputAdornment,
  Box,
  Alert,
} from "@mui/material";
import { Icon } from "@iconify/react";
import eyeFill from "@iconify/icons-eva/eye-fill";
import eyeOffFill from "@iconify/icons-eva/eye-off-fill";
import arrowBackFill from "@iconify/icons-eva/arrow-back-fill";

import Page from "../../components/Page";
import { usePGIMode } from "../../Hooks/PGIModeContext";
import { changeAdminPasswordApi } from "src/DAL/AUTH";

const passwordRequirements = [
  {
    id: "minLength",
    label: "At least 8 characters",
    test: (p) => p.length >= 8,
  },
  // {
  //   id: "hasUpper",
  //   label: "One uppercase letter",
  //   test: (p) => /[A-Z]/.test(p),
  // },
  // {
  //   id: "hasLower",
  //   label: "One lowercase letter",
  //   test: (p) => /[a-z]/.test(p),
  // },
  // { id: "hasNumber", label: "One number", test: (p) => /\d/.test(p) },
  // {
  //   id: "hasSymbol",
  //   label: "One special character",
  //   test: (p) => /[!@#$%^&*(),.?":{}|<>]/.test(p),
  // },
  {
    id: "match",
    label: "Passwords match",
    test: (p, confirm) => p === confirm && confirm.length > 0,
  },
];

const validatePassword = (password, confirmPassword = "") => {
  const validations = {};
  passwordRequirements.forEach((req) => {
    validations[req.id] =
      req.id === "match"
        ? req.test(password, confirmPassword)
        : req.test(password);
  });

  return {
    isValid: Object.values(validations).every(Boolean),
    validations,
  };
};

export default function ChangePassword() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { metaTitle } = usePGIMode();

  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [passwords, setPasswords] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [passwordValidation, setPasswordValidation] = useState({
    isValid: false,
    validations: {},
  });

  const handleTogglePassword = (field) => {
    setShowPasswords((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    const newPasswords = {
      ...passwords,
      [field]: value,
    };
    setPasswords(newPasswords);

    setPasswordValidation(
      validatePassword(newPasswords.newPassword, newPasswords.confirmPassword)
    );

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const newErrors = {};
    if (!passwords.currentPassword)
      newErrors.currentPassword = "Current password is required";
    if (!passwords.newPassword)
      newErrors.newPassword = "New password is required";
    if (!passwords.confirmPassword)
      newErrors.confirmPassword = "Confirm password is required";
    if (
      passwords.newPassword &&
      passwords.confirmPassword &&
      passwords.newPassword !== passwords.confirmPassword
    ) {
      newErrors.confirmPassword = "Passwords do not match";
    }
    if (!passwordValidation.isValid && passwords.newPassword) {
      newErrors.newPassword = "Password does not meet requirements";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    let postData = {
      old_password: passwords.currentPassword,
      new_password: passwords.newPassword,
      confirm_password: passwords.confirmPassword,
    };
    setLoading(true);
    const result = await changeAdminPasswordApi(postData);
    if (result.code === 200) {
      setPasswords({});
      setLoading(false);
      enqueueSnackbar("Password Changed Successfully", {
        variant: "success",
      });
      navigate("/dashboard");
    } else {
      setLoading(false);
      enqueueSnackbar(result.message, { variant: "error" });
    }
    setLoading(false);
  };

  return (
    <Page title={`Change Password - ${metaTitle}`}>
      <Box sx={{ mx: "auto", px: 3, py: 4 }}>
        <Typography variant="h4" sx={{ mb: 5, color: "primary.main" }}>
          Change Password
        </Typography>

        <Card sx={{ maxWidth: 750, boxShadow: 3, mx: "auto" }}>
          <CardContent
            sx={{
              px: { xs: 1.5, md: 3 },
              py: { xs: 2, md: 4 },
            }}
          >
            <form onSubmit={handleSubmit}>
              <Stack spacing={3}>
                <TextField
                  fullWidth
                  label="Current Password"
                  type={showPasswords.current ? "text" : "password"}
                  value={passwords.currentPassword}
                  onChange={handleChange("currentPassword")}
                  error={!!errors.currentPassword}
                  helperText={errors.currentPassword}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => handleTogglePassword("current")}
                        >
                          <Icon
                            icon={showPasswords.current ? eyeFill : eyeOffFill}
                          />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  fullWidth
                  label="New Password"
                  type={showPasswords.new ? "text" : "password"}
                  value={passwords.newPassword}
                  onChange={handleChange("newPassword")}
                  error={!!errors.newPassword}
                  helperText={errors.newPassword}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => handleTogglePassword("new")}>
                          <Icon
                            icon={showPasswords.new ? eyeFill : eyeOffFill}
                          />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                {passwords.newPassword && !passwordValidation.isValid && (
                  <Box
                    sx={{ px: 2, py: 1, bgcolor: "grey.100", borderRadius: 1 }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontWeight: 600, mb: 0.5, display: "block" }}
                    >
                      Password requirements:
                    </Typography>
                    <Stack spacing={0.5}>
                      {passwordRequirements.map((req) => (
                        <Stack
                          key={req.id}
                          direction="row"
                          alignItems="center"
                          spacing={1}
                        >
                          <Box
                            sx={{
                              width: 6,
                              height: 6,
                              borderRadius: "50%",
                              bgcolor: passwordValidation.validations[req.id]
                                ? "success.main"
                                : "grey.400",
                            }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              color: passwordValidation.validations[req.id]
                                ? "success.main"
                                : "text.secondary",
                            }}
                          >
                            {req.label}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Box>
                )}

                <TextField
                  fullWidth
                  label="Confirm New Password"
                  type={showPasswords.confirm ? "text" : "password"}
                  value={passwords.confirmPassword}
                  onChange={handleChange("confirmPassword")}
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => handleTogglePassword("confirm")}
                        >
                          <Icon
                            icon={showPasswords.confirm ? eyeFill : eyeOffFill}
                          />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Box
                  sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}
                >
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={loading}
                    sx={{ minWidth: 120 }}
                  >
                    {loading ? "Updating..." : "Update"}
                  </Button>
                </Box>
              </Stack>
            </form>
          </CardContent>
        </Card>
      </Box>
    </Page>
  );
}
